import {
  Flex,
  NumberInput,
  SegmentedControl,
  Space,
  Stack,
  TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import EastIcon from '@mui/icons-material/East';
import {
  PageContainer,
  PageFooter,
  PageHeader,
  SimpleStepDots,
  StepProps,
  UnsavedChangesGuard,
} from '../../../shared_components';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useStore } from '../../../shared_hooks';
import { OrderDataStoreContext } from '../stores/orderDataStore';
import { Gender, userInfo, UserInfo } from '@web/common';
import classes from '../../../app.module.css';
import { useKeycloakStore, userDataStore } from '../../../shared_stores';

export const StepData = ({ forward, back }: StepProps) => {
  const { t } = useTranslation(['payment', 'common']);
  const [isValid, setIsValid] = useState(false);
  const kcStore = useKeycloakStore();
  const orderDataStore = useStore(OrderDataStoreContext);
  const prefillData = userDataStore.userInfo
    ? {
        height: userDataStore.userInfo.height,
        yearOfBirth: userDataStore.userInfo.yearOfBirth,
        gender: userDataStore.userInfo.gender,
        email: userDataStore.userInfo.email,
        marketingConsent: userDataStore.userInfo.marketingConsent || false,
      }
    : undefined;
  const form = useForm<UserInfo>({
    initialValues: orderDataStore.orderData || prefillData,
    validateInputOnBlur: true,
    validate: zodResolver(userInfo),
  });

  useEffect(() => {
    if (form.getValues().gender === undefined) {
      form.setFieldValue('gender', Gender.male);
      form.setDirty({ gender: false });
    } else {
      setIsValid(form.isValid());
    }
  }, [form]);

  const handleChange = () => {
    setIsValid(form.isValid());
  };

  const handleSubmit = () => {
    orderDataStore.setOrderData(form.getValues());
    forward();
  };

  return (
    <PageContainer>
      <form
        onChange={handleChange}
        onSubmit={form.onSubmit(handleSubmit, console.log)}
      >
        <Flex direction={'column'} justify={'space-between'} h={'100%'}>
          <Stack
            gap={'var(--mantine-spacing-xs)'}
            classNames={{ root: classes.footerPadding }}
          >
            <PageHeader
              title={t('contact_data_title')}
              message={t('contact_data_message')}
              topElement={<SimpleStepDots step={1} max={3} />}
            />

            <NumberInput
              label={t('body_height')}
              placeholder={t('body_height') + t('common:placeholder_enter')}
              key={form.key('height')}
              {...form.getInputProps('height')}
            />
            <NumberInput
              label={t('year_of_birth')}
              placeholder={t('year_of_birth') + t('common:placeholder_enter')}
              key={form.key('year_of_birth')}
              {...form.getInputProps('yearOfBirth')}
            />
            <Space />
            <SegmentedControl
              data={[
                { value: Gender.male, label: t('gender_male') },
                { value: Gender.female, label: t('gender_female') },
                { value: Gender.nonbinary, label: t('gender_other') },
              ]}
              key={form.key('gender')}
              {...form.getInputProps('gender')}
            />
            <TextInput
              label={t('email')}
              disabled={userDataStore.userInfo && kcStore.authenticated}
              placeholder={t('email') + t('common:placeholder_enter')}
              key={form.key('email')}
              {...form.getInputProps('email')}
            />
          </Stack>
          <PageFooter
            leftOnClick={back}
            buttonText={t('to_payment')}
            buttonIcon={<EastIcon />}
            submitButton={true}
            disabled={!isValid}
          />
        </Flex>
      </form>
      <UnsavedChangesGuard when={form.isDirty()} />
    </PageContainer>
  );
};
